import store from "../../store";
import config from "@/config/brands/index";
import { get, hashSHA256 } from "../../utils";
import set from "lodash/set";
import Vue from "vue";
import { nombresPaquetes } from "../../config/constants/packagesCodes";
import { GOOGLE_DL_NAME } from "../../config/constants";
import errores from "@/config/constants/errores";

/**
 * Abstracciones para GTM
 * Acciones generales
 */
export const GTMFunctions = {
  /**
   * Realiza el push al datalayer de GTM
   * @param {Object} data a enviar al datalayer
   */
  enviar(data) {
    if (config.googleGTMId && config.googleGTMGA4) {
      // Evitar duplicados en pushes
      window[GOOGLE_DL_NAME] = window[GOOGLE_DL_NAME] || [];
      const { length, [length - 1]: lastEvent } = window[GOOGLE_DL_NAME];
      const lastElement = get(
        lastEvent,
        "eventParams.application_interactionLevel",
        1
      );
      const currentElement = get(
        data,
        "eventParams.application_interactionLevel",
        2
      );

      if (lastElement !== currentElement) {
        window[GOOGLE_DL_NAME].push({ event: "ga4.trackEvent", ...data });
        Vue.$log.info("GTM", { event: "ga4.trackEvent", ...data });
      }
    }
  },
  /**
   * Construye el pagename para GTM de acuerdo al motor
   * @param {String} level2
   * @returns {String} Pagename ya formateado
   */
  pageName(...args) {
    if (!args?.length) return GTMFunctions.appName();

    const niveles = args
      .filter(Boolean)
      .reduce((acc, cu) => (acc ? `${acc} :: ${cu}` : cu), "");

    return `${GTMFunctions.appName()} :: ${niveles}`;
  },
  /**
   * Obtiene el nombre del motor / aplicación para analytics
   * @returns {String} App Name ya formateado
   */
  appName() {
    return config.analyticsName;
  },
  /**
   * Obtiene el camino del paquete/producto ofrecido
   * @returns String - camino del producto
   */
  getCamino() {
    return get(store, "state.dataQuery.camino", null);
  },
  /**
   * Obtiene el nombre de la ruta
   * @returns String - nombre de la ruta en el circuito
   */
  getRouteName() {
    return get(store, "state.route.name", "undefined");
  },
  /**
   * Obtiene el nombre de la aplicación completo para analytics
   * @returns {String} App Name completo ya formateado
   */
  fullAppName() {
    const camino = GTMFunctions.getCamino();
    const caminoMap = {
      "3": "cliente base",
      "4": "cliente no base",
      "5": "cliente ca"
    };
    const level2 = caminoMap[camino] ?? GTMFunctions.level2();

    return `${GTMFunctions.appName()} :: ${level2}`;
  },
  /**
   * Obtiene el nombre de la ruta / vista para analytics
   * @returns {String} Nombre ruta ya formateado
   */
  level2() {
    const thankRoutes = ["thank", "cliente", "noCliente", "clienteCA"];
    let routeName = GTMFunctions.getRouteName();

    return get(
      store,
      "state.route.meta.config.analyticsName",
      thankRoutes.includes(routeName) ? "gracias" : routeName
    );
  },
  /**
   * Obtiene el nombre del camino contratado
   * @returns {String} Nombre camino ya formateado
   */
  level3() {
    const camino = GTMFunctions.getCamino();
    const levelMap = {
      "1": "ca",
      "2": "paquete"
    };

    return levelMap[camino] ?? "cliente";
  },
  /**
   * Formatear eventParams para eventos genéricos
   * @param {String} interactionLevel Nombre del elemento
   * @param {String} level3 Nombre sub-sección (como modales)
   * @returns eventParams formateado
   */
  commonEventParamsInteraction(
    interactionLevel,
    level2,
    level3,
    application_typology
  ) {
    const eventParams = {
      page_pageInfo_pageName: GTMFunctions.pageName(
        level2 || GTMFunctions.level2(),
        level3
      ),
      page_pageInfo_level1: GTMFunctions.appName(),
      page_pageInfo_level2: level2 || GTMFunctions.level2(),
      application_interactionLevel: interactionLevel
    };

    if (level3) {
      // Sub-sección (como modales)
      set(eventParams, "page_pageInfo_level3", level3);
    }

    if (application_typology) {
      // Sub-aplications (biometria, solicitud, otp, etc)
      set(eventParams, "application_typology", application_typology);
    }

    return eventParams;
  },
  /**
   * Obtiene el nombre y tipo del paquete/producto ofrecido
   * @returns Object - nombre y tipo del producto
   */
  getProductData() {
    const camino = GTMFunctions.getCamino();
    const paquete = get(
      store,
      "state.paquetesBeneficios.caracteristicas.tipoPaquete",
      null
    );

    if (camino === "1") {
      return {
        name: "caja de ahorro",
        type: "caja de ahorro"
      };
    }

    if (paquete || camino === "2") {
      return {
        name: nombresPaquetes(paquete).toLocaleLowerCase() ?? "",
        type: "paquete"
      };
    }

    return { name: "", type: "" };
  },
  /**
   * Formatear eventParams para productos y segmentos
   */
  commonEventParamsProductSegment() {
    const camino = GTMFunctions.getCamino();

    if (!["1", "2"].includes(camino)) return {};

    const { name, type } = GTMFunctions.getProductData();

    const productAttributes = {
      products_attributes_0_productName: name,
      products_attributes_0_productType: type
    };

    if (camino === "2") {
      productAttributes[
        "products_attributes_0_amount"
      ] = GTMFunctions.getMontoProducto();
    }

    return {
      ...productAttributes
    };
  },
  /**
   * Formatear eventParams para app_started y app_step_X
   * @param {String} appStep application_step
   */
  commonEventParamsAppStep(appStep, appType, pageName) {
    const eventParams = {
      page_pageInfo_pageName: pageName
        ? GTMFunctions.pageName(
            pageName,
            GTMFunctions.level2(),
            GTMFunctions.level3()
          )
        : GTMFunctions.pageName(GTMFunctions.level2(), GTMFunctions.level3()),
      application_application_name: GTMFunctions.fullAppName(),
      application_application_type: appType || "solicitud",
      application_step: appStep
    };

    const eventParamsProductSegment = GTMFunctions.commonEventParamsProductSegment();

    return { ...eventParams, ...eventParamsProductSegment };
  },

  commonEventParamsAppError(level2, errorPage, level3) {
    const eventParams = {
      page_pageInfo_pageName: GTMFunctions.pageName(
        level2 || GTMFunctions.level2(),
        level3
      ),
      page_pageInfo_level1: GTMFunctions.appName(),
      page_pageInfo_level2: level2 || GTMFunctions.level2(),
      page_pageInfo_errorPage: errorPage
    };

    if (level3) {
      // Sub-sección (como modales)
      set(eventParams, "page_pageInfo_level3", level3);
    }

    return eventParams;
  },

  /**
   * Obtiene el nombre del paquete/producto ofrecido
   * @returns String | undefined - monto ofrecido del paquete
   */
  getMontoProducto() {
    const paquete = store.getters.getPaquetesBeneficios();
    const limite = get(paquete, "caracteristicas.limite", undefined);

    if (limite === undefined || limite === null) {
      return undefined;
    }

    // Convertir formato con separación de miles y coma decimal
    const valorSanitizado = String(limite)
      .replace(/\./g, "")
      .replace(",", ".");

    return parseFloat(valorSanitizado);
  },

  getContactData() {
    const email = get(store, "state.consulta.email", "");
    const celularNormalizado = get(
      store,
      "state.consulta.celular.normalizado",
      {}
    );
    const celularNumero = get(store, "state.consulta.celular.numero", "");
    const phoneFromNormalizado =
      (celularNormalizado.caracteristica || "") +
      (celularNormalizado.nro || "");

    return {
      email,
      phone: phoneFromNormalizado || celularNumero
    };
  },

  async getHashedContactData() {
    const { email, phone } = GTMFunctions.getContactData();
    const [hashedEmail, hashedPhone] = await Promise.all([
      hashSHA256(email),
      hashSHA256(phone)
    ]);

    return {
      email: hashedEmail,
      phone: hashedPhone
    };
  }
};

export const GTM = {
  enable() {},
  init() {
    // GTM se inyecta directamente en el template index.html
    Vue.$log.info("GTM INIT");
  },
  wait() {
    return true;
  },
  /**
   * Evento de links, clicks a botones
   * @param {Object} eventData (name, level3)
   */
  interaction_link({ name, level2, level3 }) {
    const eventParams = GTMFunctions.commonEventParamsInteraction(
      name,
      level2,
      level3
    );

    GTMFunctions.enviar({
      eventName: "interaction_link",
      eventParams
    });
  },
  async to_oferta() {
    const eventParamsBase = GTMFunctions.commonEventParamsAppStep(
      "0 oferta",
      config.analyticsType
    );
    const {
      email: hashedEmail,
      phone: hashedPhone
    } = await GTMFunctions.getHashedContactData();

    const eventParams = {
      ...eventParamsBase,
      email: hashedEmail,
      phone: hashedPhone
    };

    GTMFunctions.enviar({
      eventName: "app_step_0",
      eventParams
    });
  },
  to_paso1() {
    const eventParams = GTMFunctions.commonEventParamsAppStep(
      "0 start",
      config.analyticsType
    );

    GTMFunctions.enviar({
      eventName: "app_started",
      eventParams
    });
  },
  to_paso2() {
    const eventParams = GTMFunctions.commonEventParamsAppStep(
      "2 datos laborales",
      config.analyticsType
    );

    GTMFunctions.enviar({
      eventName: "app_step_2",
      eventParams
    });
  },
  to_paso3() {
    const eventParams = GTMFunctions.commonEventParamsAppStep(
      "3 actividad",
      config.analyticsType
    );

    GTMFunctions.enviar({
      eventName: "app_step_3",
      eventParams
    });
  },
  to_paso4(eventData) {
    let eventParams;
    const analyticsName = get(eventData, "to.meta.config.analyticsName", "");
    if (analyticsName) {
      eventParams = GTMFunctions.commonEventParamsAppStep(
        "4 " + analyticsName,
        config.analyticsType
      );
      GTMFunctions.enviar({
        eventName: "app_step_4",
        eventParams
      });
    }
  },
  async app_completed() {
    const routeName = GTMFunctions.getRouteName();

    const stepMap = {
      gracias: "5 gracias",
      thank: "5 gracias",
      cliente: "3 gracias",
      clienteCA: "3 gracias",
      noCliente: "4 gracias"
    };

    const step = stepMap[routeName] ?? "";

    const eventParams = GTMFunctions.commonEventParamsAppStep(
      step,
      config.analyticsType
    );

    const {
      email: hashedEmail,
      phone: hashedPhone
    } = await GTMFunctions.getHashedContactData();

    const eventParamsWithContact = {
      ...eventParams,
      email: hashedEmail,
      phone: hashedPhone
    };

    const includeOperationNumber =
      routeName === "gracias" || routeName === "thank";

    const application_operationNumber = includeOperationNumber
      ? get(store, "state.dataOferta.nroDeTramite", "")
      : null;

    GTMFunctions.enviar({
      eventName: "app_completed",
      eventParams: application_operationNumber
        ? { ...eventParamsWithContact, application_operationNumber }
        : eventParamsWithContact
    });
  },
  to_mensaje(eventData) {
    let eventParams;
    let error = errores[get(store, "state.route.params.mje", "undefined")];
    switch (eventData.to.params.mje) {
      case "error-1":
        eventParams = GTMFunctions.commonEventParamsAppError(
          error.analyticsModule,
          "error tecnico"
        );
        break;
      case "error-2":
        eventParams = GTMFunctions.commonEventParamsAppError(
          error.analyticsModule,
          "individuo negativo"
        );
        break;
      case "error-3":
        eventParams = GTMFunctions.commonEventParamsAppError(
          error.analyticsModule,
          "rechazado"
        );
        break;
      default:
        eventParams = GTMFunctions.commonEventParamsAppError(
          eventData.to.params.mje,
          "error general"
        );
    }

    GTMFunctions.enviar({
      eventName: "server_error",
      eventParams
    });
  }
};
export default GTM;
